import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import portalApi, { errMsg } from "../../lib/portalApi";

const Shell = ({ title, subtitle, children, testid }) => (
  <div className="min-h-screen flex items-center justify-center px-5 py-10" style={{ background: "var(--mf-bg)" }} data-testid={testid}>
    <div className="w-full max-w-md mf-fade-in">
      <div className="flex items-center gap-2 mb-8">
        <img src="/icons/icon-192.png" alt="Clinician Well logo" className="w-9 h-9 rounded-xl" />
        <span className="font-serif text-xl" style={{ color: "var(--mf-primary)" }}>Clinician Well</span>
        <span className="text-xs px-2 py-0.5 rounded-full ml-1" style={{ background: "var(--mf-muted)", color: "var(--mf-text-muted)" }}>Client Portal</span>
      </div>
      <h1 className="font-serif text-4xl mb-2">{title}</h1>
      <p className="text-sm mb-8" style={{ color: "var(--mf-text-muted)" }}>{subtitle}</p>
      {children}
    </div>
  </div>
);

const input = "w-full px-4 py-3 rounded-xl outline-none text-sm";
const muted = { background: "var(--mf-surface)", border: "1px solid var(--mf-muted)" };

export function PortalLogin() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await portalApi.post("/portal/login", form);
      localStorage.setItem("cw_portal_token", data.token);
      navigate("/portal");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Welcome back" subtitle="Sign in to book sessions, complete forms, pay and message your clinician." testid="portal-login-page">
      <form onSubmit={submit} className="space-y-3">
        <input type="email" required placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} style={muted} data-testid="portal-login-email" />
        <input type="password" required placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={input} style={muted} data-testid="portal-login-password" />
        <button type="submit" disabled={busy} className="mf-btn-primary w-full py-3" data-testid="portal-login-submit">{busy ? "Signing in…" : "Sign in"}</button>
      </form>
      <Link to="/portal/forgot" className="block text-sm mt-4" style={{ color: "var(--mf-primary)" }} data-testid="portal-forgot-link">Forgot your password?</Link>
      <p className="text-xs mt-6" style={{ color: "var(--mf-text-muted)" }}>New here? Your clinician will email you an invitation link. Clinicians: <Link to="/login" className="underline">sign in here</Link>.</p>
    </Shell>
  );
}

export function PortalAccept() {
  const [params] = useSearchParams();
  const [pw, setPw] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      const { data } = await portalApi.post("/portal/accept", { token: params.get("token") || "", password: pw.a });
      localStorage.setItem("cw_portal_token", data.token);
      toast.success("Your portal is ready");
      navigate("/portal");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Set up your portal" subtitle="Create a password (8+ characters) to finish setting up your secure client portal." testid="portal-accept-page">
      <form onSubmit={submit} className="space-y-3">
        <input type="password" required minLength={8} placeholder="Create password" value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} className={input} style={muted} data-testid="portal-accept-password" />
        <input type="password" required minLength={8} placeholder="Confirm password" value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} className={input} style={muted} data-testid="portal-accept-confirm" />
        <button type="submit" disabled={busy} className="mf-btn-primary w-full py-3" data-testid="portal-accept-submit">{busy ? "Saving…" : "Create my portal"}</button>
      </form>
    </Shell>
  );
}

export function PortalForgot() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await portalApi.post("/portal/forgot", { email, origin_url: window.location.origin }); setSent(true); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Forgot password" subtitle="Enter the email you use for the portal and we'll send you a reset link (valid for 1 hour)." testid="portal-forgot-page">
      {sent ? (
        <div className="p-4 rounded-xl text-sm" style={muted} data-testid="portal-forgot-sent">If that email has a portal account, a reset link is on its way. Check your inbox (and spam folder).</div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={input} style={muted} data-testid="portal-forgot-email" />
          <button type="submit" disabled={busy} className="mf-btn-primary w-full py-3" data-testid="portal-forgot-submit">{busy ? "Sending…" : "Send reset link"}</button>
        </form>
      )}
      <Link to="/portal/login" className="block text-sm mt-4" style={{ color: "var(--mf-primary)" }} data-testid="portal-back-login">Back to sign in</Link>
    </Shell>
  );
}

export function PortalReset() {
  const [params] = useSearchParams();
  const [pw, setPw] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await portalApi.post("/portal/reset", { token: params.get("token") || "", password: pw.a });
      toast.success("Password updated — please sign in");
      navigate("/portal/login");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Choose a new password" subtitle="Use at least 8 characters." testid="portal-reset-page">
      <form onSubmit={submit} className="space-y-3">
        <input type="password" required minLength={8} placeholder="New password" value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} className={input} style={muted} data-testid="portal-reset-password" />
        <input type="password" required minLength={8} placeholder="Confirm new password" value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} className={input} style={muted} data-testid="portal-reset-confirm" />
        <button type="submit" disabled={busy} className="mf-btn-primary w-full py-3" data-testid="portal-reset-submit">{busy ? "Saving…" : "Update password"}</button>
      </form>
    </Shell>
  );
}
